import {Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Step} from '../../../models/convo';
import {User} from '../../../../shared/models/user';
import {StepId, UUID} from '../../../models/uuid';
import {Alias, getAliasProvider} from '../../../models/llm/llm';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import { ConvoService, MAX_STEPS_PER_GROUP } from 'src/moti/services/convo.service';
import {ConvoBubbleComponent} from '../convo-bubble/convo-bubble.component';
import {ConvoBubbleOptionsComponent} from '../convo-bubble-options/convo-bubble-options.component';
import {ConvoStepMetadataComponent} from '../convo-step-metadata/convo-step-metadata.component';
import { SessionService } from '../../../../core/services/session.service';


@Component({
  selector: 'moti-convo-bubble-group',
  templateUrl: './convo-bubble-group.component.html',
  styleUrl: './convo-bubble-group.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ConvoBubbleComponent,
    ConvoBubbleOptionsComponent,
    ConvoStepMetadataComponent,
  ],
})
export class ConvoBubbleGroupComponent implements OnInit, OnDestroy {
  /**
   * Defines whether rendering of all of the component's elements should happen yet
   */
  @Input() readyToRender: boolean = true;
  /**
   * Defines whether the various actions, like add and delete, should be made available yet
   */
  @Input() readyToAction: boolean = true;

  @Input() steps$: Observable<Step>;
  @Input() stepDeletions$: Observable<StepId>;
  @Input() stepPrimaryChanges$: Observable<StepId>;
  @Input() isLatestStep: boolean = false;
  @Input() user: User;
  @Input() stagger: boolean = true;
  @Input() showModel: boolean = false;
  @Input() modelOrder: Alias[]; // should reflect current model selection, in order, by user

  private sub1: Subscription;
  private sub2: Subscription;
  private sub3: Subscription;

  step$Arr: BehaviorSubject<Step>[] = [];
  stepArr: Step[] = [];
  stepIdOrder: { [id: string]: number };

  primaryStep: Step;

  selectingPrimaryStep: UUID;
  deletingStep: UUID;
  waitingForAddedStep: boolean = false;


  constructor(
    private convoSvc: ConvoService,
    protected sess: SessionService,
  ) {
  }


  ngOnInit() {
    this.sub1 = this.steps$.subscribe({
      next: step => {
        this.mergeStep(step);
      },
    });

    this.sub2 = this.stepDeletions$.subscribe({
      next: stepId => {
        this.removeStep(stepId);
      },
    });

    this.sub3 = this.stepPrimaryChanges$.subscribe({
      next: stepId => {
        this.autoSetPrimaryStep(stepId);
      },
    });
  }


  //
  // ngOnChanges(_: SimpleChanges) {
  //   this.sortSteps();
  // }


  ngOnDestroy() {
    this.sub1?.unsubscribe();
    this.sub2?.unsubscribe();
    this.sub3?.unsubscribe();
  }


  private mergeStep(step: Step) {
    const current = this.stepArr.findIndex(s => s.id === step.id);

    if (current > -1) {
      let shouldMerge: boolean = false;

      if (!step.partialOrder) {
        shouldMerge = true;
      } else if (this.stepArr[current].partialOrder && this.stepArr[current].partialOrder < step.partialOrder) {
        shouldMerge = true;
      }

      if (shouldMerge) {
        this.step$Arr[current].next(step);

        // Do this instead of replacing the whole object to avoid re-rendering the
        // spinning wheel every time a new partial step is received
        this.stepArr[current].errorAt = step.errorAt;
        this.stepArr[current].partialOrder = step.partialOrder;
      }
    } else {
      this.waitingForAddedStep = false;
      this.step$Arr.push(new BehaviorSubject<Step>(step));
      this.stepArr.push(step);
      this.sortSteps();
    }
  }


  private removeStep(stepId: UUID) {
    const index = this.stepArr.findIndex(s => s.id === stepId);

    if (index == -1) {
      return;
    }

    this.step$Arr[index].complete();
    this.step$Arr.splice(index, 1);
    this.stepArr.splice(index, 1);

    this.deletingStep = undefined;
  }


  /**
   * Looks at the steps and determines which one should be designated as primary for
   * setting to this.primaryStep, and then does so.
   *
   * @param stepId
   * @private
   */
  private autoSetPrimaryStep(stepId: UUID) {
    for (let step of this.stepArr) {
      step.isAlternate = step.id != stepId;
    }

    this.selectingPrimaryStep = undefined;
    this.sortSteps();
  }


  private sortSteps() {
    const sortedStepIds: StepId[] = [];
    const primaryStep = this.stepArr.find(s => !s.isAlternate);
    const altSteps = this.stepArr.filter(s => s.isAlternate);

    if (primaryStep) {
      sortedStepIds.push(primaryStep.id);
      this.primaryStep = primaryStep;
    }

    if (this.modelOrder?.length && altSteps.length) {
      const altModelOrder = this.modelOrder.slice(1);

      const oldAlts: Step[] = altSteps.map(s => s);
      const newAlts: Step[] = altSteps.map(_ => null);

      // Match up matching indexes, leaving unmatching indexes' values null
      for (let i = 0; i < newAlts.length; i++) {
        if (oldAlts[i].config.modelAlias === altModelOrder[i]) {
          newAlts[i] = oldAlts[i];
        }
      }

      const unusedAlts: Step[] = [];

      // Find the indexes we left null; take the steps and store them in an
      // array
      for (let i = 0; i < newAlts.length; i++) {
        if (!newAlts[i]) {
          unusedAlts.push(oldAlts[i]);
        }
      }

      // Take the unused steps and fill them in where we still have null
      // values in newAlts
      for (let i = 0; i < newAlts.length; i++) {
        if (!newAlts[i]) {
          newAlts[i] = unusedAlts.pop();
        }
      }

      sortedStepIds.push(...newAlts.map(s => s.id));
    } else {
      sortedStepIds.push(...altSteps.map(s => s.id));
    }

    const order: { [id: string]: number } = {};

    for (let i = 0; i < sortedStepIds.length; i++) {
      order[sortedStepIds[i]] = i + 1;
    }

    this.stepIdOrder = order;
  }


  get isShowingMultipleSteps(): boolean {
    return this.stepArr.length > 1 && this.stepArr[0].createdByProvider;
  }


  onSelectPrimary(stepId: UUID) {
    if (this.selectingPrimaryStep) {
      return;
    }

    this.selectingPrimaryStep = stepId;
    this.convoSvc.sendStepUpdate(stepId, this.primaryStep.convoId, false);
  }


  onClickDelete(stepId: UUID) {
    if (this.deletingStep) {
      return;
    }

    const step = this.stepArr.find(s => s.id === stepId);

    if (!step.isAlternate && this.stepArr.length > 1) {
      throw new Error('cannot delete a primary step if alternate steps exist');
    }

    this.deletingStep = stepId;
    this.convoSvc.sendStepDeletion(stepId, this.primaryStep.convoId);
  }


  onClickAdd(alias: Alias) {
    this.waitingForAddedStep = true;

    this.convoSvc.sendStepGroupAddition(this.primaryStep.id, this.primaryStep.convoId, {
      modelAlias: alias,
      provider: getAliasProvider(alias),
    });
  }


  protected readonly MAX_STEPS_PER_GROUP = MAX_STEPS_PER_GROUP;
}
